import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import ShareBar from "@/components/feature/ShareBar";
import { movies, movieTopics } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

export default function MovieDetail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggle } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: movie ? `${movie.title} - 91电影` : "影片详情 - 91电影",
    description: movie
      ? `${movie.title}（${movie.year}）影片简介、评分与演职人员信息，${movie.synopsis.slice(0, 60)}`
      : "91电影影片详情页",
    keywords: movie ? `${movie.title},${movie.genres.join(",")},影片推荐,91电影` : "91电影,影片推荐",
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2400);
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-8">
        <EmptyState
          icon="ri-film-line"
          title="影片不存在或已下架"
          description="你访问的影片信息可能已更新，去看看其他推荐影片吧。"
          action={
            <Link
              to="/category/recommend"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md px-5 py-2.5 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回影片推荐
            </Link>
          }
        />
      </div>
    );
  }

  const favorite = isFavorite(movie.id);
  const related = movies.filter((m) => m.id !== movie.id).slice(0, 4);
  const topic = movieTopics.find((t) => t.title === movie.topic);

  const handleFavorite = () => {
    const added = toggle(movie.id);
    showToast(added ? "已添加收藏" : "已取消收藏");
  };

  const infoRows = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "上映年份", value: String(movie.year) },
    { label: "片长", value: movie.duration },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-8 animate-fade-up">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影片推荐", to: "/category/recommend" },
          { label: movie.title },
        ]}
      />

      <div className="mt-6 bg-background-50 rounded-lg border border-background-200 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0">
          <div className="relative bg-secondary-200">
            <div className="w-full aspect-[2/3] md:h-full">
              <img
                src={movie.poster}
                alt={`${movie.title} 影片封面`}
                title={`${movie.title} 影片详情 91电影`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="p-5 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
                  {movie.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {movie.genres.map((g) => (
                    <span key={g} className="text-xs bg-secondary-100 text-secondary-800 rounded px-2.5 py-1">
                      {g}
                    </span>
                  ))}
                  <span className="text-xs bg-accent-100 text-accent-900 rounded px-2.5 py-1">{movie.region}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="font-heading font-extrabold text-3xl text-accent-600 leading-none">
                    {movie.rating.toFixed(1)}
                  </p>
                  <p className="text-xs text-foreground-400 mt-1">示范评分</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <span className="text-foreground-400 shrink-0 w-16">{row.label}</span>
                  <span className="text-foreground-800">{row.value}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-7 relative">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 text-sm font-bold rounded-md px-5 py-2.5 transition-colors whitespace-nowrap cursor-pointer ${
                  favorite
                    ? "bg-accent-500 hover:bg-accent-600 text-secondary-950"
                    : "bg-primary-500 hover:bg-primary-600 text-background-50"
                }`}
              >
                <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorite ? "已收藏" : "收藏影片"}
              </button>
              <ShareBar />
              {toast && (
                <div className="absolute -top-12 left-0 z-20 bg-secondary-950 text-background-50 text-xs rounded-md px-3 py-2 animate-fade-in whitespace-nowrap">
                  {toast}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-10 max-w-4xl">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-1.5 h-6 rounded-full bg-accent-500" />
          <h2 className="font-heading font-bold text-xl text-foreground-950">影片简介</h2>
        </div>
        <p className="text-base leading-[1.9] text-foreground-800">{movie.synopsis}</p>
      </section>

      {topic && (
        <section className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            <h2 className="font-heading font-bold text-xl text-foreground-950">所属专题</h2>
          </div>
          <Link
            to="/category/recommend"
            className="group flex flex-col md:flex-row items-stretch gap-0 bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
          >
            <div className="md:w-64 h-40 md:h-auto shrink-0 bg-secondary-200 overflow-hidden">
              <img
                src={topic.cover}
                alt={`${topic.title} 专题封面`}
                title={`${topic.title} 影片专题 91电影`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5 md:p-6 flex flex-col justify-center">
              <span className="inline-flex w-fit items-center gap-1 bg-accent-100 text-accent-900 text-xs font-bold rounded px-2 py-1 mb-3">
                <i className="ri-bookmark-3-line" />
                专题影单
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-950">{topic.title}</h3>
              <p className="text-sm text-foreground-500 mt-2 line-clamp-2">{topic.description}</p>
              <span className="inline-flex items-center gap-1 text-primary-700 text-sm font-medium mt-3">
                进入专题
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        </section>
      )}

      <section className="mt-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-accent-500" />
          <h2 className="font-heading font-bold text-xl text-foreground-950">相关推荐影片</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {related.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>
      </section>
    </div>
  );
}